import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpRequest,
  HttpContext,
  HttpContextToken,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LoggerService } from '../../../core/logging/logger.service';

// Token to skip interceptors for S3 uploads
export const SKIP_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
import config from '../../../../config.js';
import {
  BcrStatusDto,
  LegalEntityDetailDto,
  LegalEntityDto,
  EntityIdentificationDto,
  ContactInformationDto,
  DdqSpecificFieldsDto,
  CountryGroup,
  UploadStatus,
  DocumentType,
  UploadUrlRequest,
  PresignedPost,
  PresignedUploadResponse,
  ConfirmUploadResponse,
  DocumentFile,
  DownloadUrlResponse,
  DdqUploadRequest,
  DdqUploadResponse,
  DdqConfirmUploadRequest,
  DdqConfirmUploadResponse,
  DdqActionResponse,
} from '../models/legal-entity.models';
import { RemediationActionDto } from '../../../shared/models/remediation-action.model';
import { LoaDocument } from '../models/loa-document.model';

// Re-export all models for backward compatibility
export type {
  BcrStatusDto,
  LegalEntityDetailDto,
  LegalEntityDto,
  EntityIdentificationDto,
  ContactInformationDto,
  DdqSpecificFieldsDto,
  CountryGroup,
  DocumentType,
  UploadUrlRequest,
  PresignedPost,
  PresignedUploadResponse,
  ConfirmUploadResponse,
  DocumentFile,
  DownloadUrlResponse,
};
export { UploadStatus };

@Injectable({ providedIn: 'root' })
export class LegalEntityService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  // Fallback allows tests/local run if config not populated yet
  private readonly endpoint = `${this.base}/legal_entities`;

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  getLegalEntities(): Observable<CountryGroup[]> {
    return this.http.get<CountryGroup[]>(this.endpoint);
  }

  /**
   * Fetch the list of BCR status objects from the backend.
   */
  getBcrStatuses(): Observable<BcrStatusDto[]> {
    const base = this.base;
    const endpoint = `${base}/legal_entities/bcr_status`;
    return this.http.get<BcrStatusDto[]>(endpoint);
  }

  getLegalEntityDetail(entityId: number): Observable<LegalEntityDetailDto> {
    return this.http.get<LegalEntityDetailDto>(`${this.endpoint}/detail/${entityId}`);
  }

  /**
   * Update entity identification information for a legal entity.
   * Endpoint: PUT /legal_entities/{legal_entity_id}/entity_identification
   */
  updateEntityIdentification(
    legalEntityId: number,
    data: { registration_number: string; signatories: string },
  ): Observable<EntityIdentificationDto> {
    return this.http.put<EntityIdentificationDto>(
      `${this.endpoint}/${legalEntityId}/entity_identification`,
      data,
    );
  }
  /**
   * Update contact information for a legal entity.
   * Endpoint: PUT /legal_entities/contacts
   */
  updateContactInformation(
    data: Array<{
      id: number;
      contact_name: string;
      contact_email: string;
      phone: string | null;
      legal_entity_id: number;
      created_date: number;
      last_updated_date: number | null;
    }>,
  ): Observable<ContactInformationDto[]> {
    return this.http.put<ContactInformationDto[]>(`${this.endpoint}/contacts`, data);
  }

  /**
   * Get DDQ-specific fields for a legal entity.
   * Endpoint: GET /legal_entities/{legal_entity_id}/ddq-specific-fields
   *
   * @param legalEntityId - Legal entity ID
   * @returns Observable with the DDQ-specific fields
   */
  getDdqSpecificFields(legalEntityId: number): Observable<DdqSpecificFieldsDto> {
    return this.http.get<DdqSpecificFieldsDto>(
      `${this.endpoint}/${legalEntityId}/ddq-specific-fields`,
    );
  }

  /**
   * Create DDQ-specific fields for a legal entity.
   * Endpoint: POST /legal_entities/ddq-specific-fields
   *
   * @param data - DDQ fields data with nominee, contact email, and legal entity ID
   * @returns Observable with the created DDQ fields
   */
  createDdqSpecificFields(data: {
    id: null;
    ddq_nominee: string;
    contact_email: string;
    legal_entity_id: number;
    created_date: number;
    last_updated_date: null;
  }): Observable<DdqSpecificFieldsDto> {
    return this.http.post<DdqSpecificFieldsDto>(`${this.endpoint}/ddq-specific-fields`, data);
  }

  /**
   * Update DDQ-specific fields for a legal entity.
   * Endpoint: PUT /legal_entities/ddq-specific-fields
   *
   * @param data - Complete DDQ fields data including all fields (id is sent in payload)
   * @returns Observable with the updated DDQ fields
   */
  updateDdqSpecificFields(data: {
    id: number;
    ddq_nominee: string;
    contact_email: string;
    legal_entity_id: number;
    created_date: number;
    last_updated_date: number;
  }): Observable<DdqSpecificFieldsDto> {
    return this.http.put<DdqSpecificFieldsDto>(`${this.endpoint}/ddq-specific-fields`, data);
  }

  /**
   * Notify DDQ nominee and send them the default DDQ file.
   * Endpoint: POST /legal_entities/ddq-specific-fields/notify
   *
   * @param data - DDQ fields data to notify the nominee
   * @returns Observable with the response
   */
  notifyDdqNominee(data: {
    id: number;
    ddq_nominee: string;
    contact_email: string;
    legal_entity_id: number;
    created_date: number;
    last_updated_date: number;
  }): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.endpoint}/ddq-specific-fields/notify`,
      data,
    );
  }

  /**
   * Create new contact information for a legal entity.
   * Endpoint: POST /legal_entities/contacts
   */
  createContactInformation(data: {
    id: number;
    contact_name: string;
    contact_email: string;
    phone: string | null;
    legal_entity_id: number;
    created_date: number;
    last_updated_date: number;
  }): Observable<ContactInformationDto> {
    return this.http.post<ContactInformationDto>(`${this.endpoint}/contacts`, data);
  }

  /**
   * Get contact information for a legal entity.
   * Endpoint: GET /legal_entities/<legal_entity_id>/contacts
   */
  getContactInformation(legalEntityId: number): Observable<ContactInformationDto[]> {
    return this.http.get<ContactInformationDto[]>(`${this.endpoint}/${legalEntityId}/contacts`);
  }

  /**
   * Check whether a user has permission for a given module/role.
   * Expects body: { prid_user, role_name: string[], module_name: string[] }
   */
  hasPermission(pridUser: string) {
    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: ['BCR - PST member'],
      module_name: ['Legal Entity List'],
    };
    return this.http.post<{ has_permission: boolean }>(endpoint, body);
  }

  /**
   * Request a change of BCR status for a legal entity.
   * Expects payload: { user_prid, legal_entity_id, bcr_status_id, link }
   */
  changeBcrStatus(userPrid: string, legalEntityId: number, bcrStatusId: number, link: string) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status_request`;
    const body = {
      user_prid: userPrid,
      legal_entity_id: legalEntityId,
      bcr_status_id: bcrStatusId,
      link,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }

  /**
   * Approve or reject a BCR status change request.
   * Expects payload with all notification data + request_status: 'Approved' | 'Rejected'
   */
  updateBcrStatusRequest(
    id: number,
    author: string,
    approver: string,
    createdDate: number,
    requestStatus: 'Approved' | 'Rejected',
    legalEntityId: number,
    newBcrStatusId: number,
    previousBcrStatusId: number,
    message: string = '',
    lastUpdatedDate: number | null = null,
  ) {
    const endpoint = `${this.base}/legal_entities/change_bcr_status`;
    const body = {
      id,
      author,
      message,
      approver,
      created_date: createdDate,
      request_status: requestStatus,
      legal_entity_id: legalEntityId,
      last_updated_date: lastUpdatedDate,
      new_bcr_status_id: newBcrStatusId,
      previous_bcr_status_id: previousBcrStatusId,
    };
    return this.http.put<{ success?: boolean; message?: string }>(endpoint, body);
  }

  // ========== Document Management Methods ==========

  /**
   * Get all available document types (predefined templates)
   * Endpoint: GET /legal_entities/document_types
   */
  getDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<DocumentType[]>(`${this.endpoint}/document_types`);
  }

  /**
   * Get B&CS assessment document types
   * Endpoint: GET /legal_entities/b_and_cs_assessment_document_types
   */
  getBncsAssessmentDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<DocumentType[]>(`${this.endpoint}/b_and_cs_assessment_document_types`);
  }

  /**
   * Generate presigned upload URL for B&CS assessment document
   * Endpoint: POST /legal_entities/{id}/b_and_cs_assessment/upload-url
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Upload request with document_type_name and optional comments
   * @returns Presigned POST URL data and document_file_id for confirmation
   */
  generateBncsAssessmentUploadUrl(
    legalEntityId: number,
    request: UploadUrlRequest,
  ): Observable<PresignedUploadResponse> {
    return this.http.post<PresignedUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/b_and_cs_assessment/upload-url`,
      request,
    );
  }

  /**
   * Get all B&CS assessment documents for a specific legal entity
   * Endpoint: GET /legal_entities/{id}/b_and_cs_assessment_documents
   *
   * @param legalEntityId - Legal entity ID
   * @returns Array of B&CS assessment document files with metadata
   */
  getBncsAssessmentDocuments(legalEntityId: number): Observable<DocumentFile[]> {
    return this.http.get<DocumentFile[]>(
      `${this.base}/legal_entities/${legalEntityId}/b_and_cs_assessment_documents`,
    );
  }
  /**
   * Get DDQ documents for a legal entity
   * Endpoint: GET /legal_entities/{id}/ddq_documents
   *
   * @param legalEntityId - Legal entity ID
   * @returns Object with documents array containing DDQ document files with metadata
   */
  getDdqDocuments(legalEntityId: number): Observable<{ documents: DocumentFile[] }> {
    return this.http.get<{ documents: DocumentFile[] }>(
      `${this.base}/legal_entities/${legalEntityId}/ddq_documents`,
    );
  }

  /**
   * Get Letter of Adherence documents for a legal entity
   * Endpoint: GET /legal_entities/{id}/loa_documents
   *
   * @param legalEntityId - Legal entity ID
   * @returns Array of LoA document files with metadata
   */
  getLoaDocuments(legalEntityId: number): Observable<LoaDocument[]> {
    return this.http.get<LoaDocument[]>(
      `${this.base}/legal_entities/${legalEntityId}/loa_documents`,
    );
  }

  /**
   * Generate presigned upload URL for direct browser-to-S3 upload
   * Endpoint: POST /legal_entities/{id}/documents/upload-url
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Upload request with document_type_name and optional comments
   * @returns Presigned POST URL data and document_file_id for confirmation
   */
  generateUploadUrl(
    legalEntityId: number,
    request: UploadUrlRequest,
  ): Observable<PresignedUploadResponse> {
    return this.http.post<PresignedUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/documents/upload-url`,
      request,
    );
  }

  /**
   * Generate presigned upload URL for LOA documents (LOA Signed 1/2, LOA Signed 2/2, LOI Completed)
   * Endpoint: POST /legal_entities/{id}/loa/upload-url
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Upload request with document_type_name and optional comments
   * @returns Presigned POST URL data and document_file_id for confirmation
   */
  generateLoaUploadUrl(
    legalEntityId: number,
    request: UploadUrlRequest,
  ): Observable<PresignedUploadResponse> {
    return this.http.post<PresignedUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/loa/upload-url`,
      request,
    );
  }

  /**
   * Confirm a LoA document upload
   * Endpoint: POST /legal_entities/{legal_entity_id}/loa/confirm-upload
   *
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID from generateLoaUploadUrl response
   * @param documentTypeName - Document type name used during upload
   * @returns Confirmation response
   */
  confirmLoaUpload(
    legalEntityId: number,
    documentFileId: number,
    documentTypeName: string,
  ): Observable<{
    document_file_id: number;
    legal_entity_id: number;
    document_type_name: string;
    date_loa_signed_updated: boolean;
    message: string;
  }> {
    return this.http.post<{
      document_file_id: number;
      legal_entity_id: number;
      document_type_name: string;
      date_loa_signed_updated: boolean;
      message: string;
    }>(`${this.base}/legal_entities/${legalEntityId}/loa/confirm-upload`, {
      document_file_id: documentFileId,
      document_type_name: documentTypeName,
    });
  }

  /**
   * Delete a LoA document
   * Endpoint: DELETE /legal_entities/{legal_entity_id}/loa/{document_file_id}
   *
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID to delete
   * @returns Response with deletion confirmation
   */
  deleteLoaDocument(
    legalEntityId: number,
    documentFileId: number,
  ): Observable<{ message: string; document_file_id: number }> {
    return this.http.delete<{ message: string; document_file_id: number }>(
      `${this.base}/legal_entities/${legalEntityId}/loa/${documentFileId}`,
    );
  }

  /**
   * Upload file directly to S3 using presigned POST URL
   * This method constructs FormData with presigned fields and file
   *
   * CRITICAL: Fields must be appended BEFORE the file
   * CRITICAL: Must skip auth interceptor - S3 uses presigned URL authentication
   * CRITICAL: Content-Type must match the one used when generating presigned URL
   *
   * @param file - File to upload
   * @param presignedPost - Presigned POST data from generateUploadUrl
   * @returns Observable with upload progress events
   */
  uploadToS3(
    file: File,
    presignedPost: PresignedUploadResponse['presigned_post'],
  ): Observable<HttpEvent<unknown>> {
    const formData = new FormData();

    const fieldOrder = [
      'key',
      'bucket',
      'Policy',
      'X-Amz-Algorithm',
      'X-Amz-Credential',
      'X-Amz-Date',
      'X-Amz-Security-Token',
      'X-Amz-Signature',
      'Content-Type',
    ];

    fieldOrder.forEach((key) => {
      if (presignedPost.fields[key] !== undefined) {
        formData.append(key, presignedPost.fields[key]);
      }
    });

    // If Content-Type is not in presigned fields, add it manually based on file type
    if (!presignedPost.fields['Content-Type']) {
      formData.append('Content-Type', file.type || 'application/octet-stream');
    }

    // Append any remaining fields not in the order list
    Object.keys(presignedPost.fields).forEach((key) => {
      if (!fieldOrder.includes(key)) {
        const value = presignedPost.fields[key];
        if (value !== undefined) {
          formData.append(key, value);
        }
      }
    });
    formData.append('file', file, file.name);
    // Create request with progress tracking and SKIP interceptor
    // Do NOT set Content-Type header - let browser set it with boundary for multipart/form-data
    const req = new HttpRequest('POST', presignedPost.url, formData, {
      reportProgress: true,
      context: new HttpContext().set(SKIP_INTERCEPTOR, true),
    });

    return this.http.request(req);
  }

  /**
   * Validate B&CS assessment file after S3 upload
   * Endpoint: POST /legal_entities/{legal_entity_id}/b_and_cs_assessment/validate
   *
   * @param legalEntityId - Legal entity ID
   * @param s3Uri - S3 URI of the uploaded file (s3://bucket/key)
   * @returns Validation response with document metadata
   */
  validateBncsAssessment(legalEntityId: number, s3Uri: string): Observable<ConfirmUploadResponse> {
    return this.http.post<ConfirmUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/b_and_cs_assessment/validate`,
      { s3_uri: s3Uri },
    );
  }

  /**
   * Confirm successful S3 upload and activate document record
   * Endpoint: POST /documents/{document_file_id}/confirm
   *
   * @param documentFileId - Document file ID from generateUploadUrl response
   * @returns Confirmation data with S3 metadata
   */
  confirmUpload(documentFileId: number): Observable<ConfirmUploadResponse> {
    return this.http.post<ConfirmUploadResponse>(
      `${this.base}/documents/${documentFileId}/confirm`,
      {},
    );
  }

  /**
   * Get all documents for a specific legal entity
   * Endpoint: GET /legal_entities/{id}/documents
   *
   * @param legalEntityId - Legal entity ID
   * @returns Array of document files with metadata
   */
  getLegalEntityDocuments(legalEntityId: number): Observable<DocumentFile[]> {
    return this.http.get<DocumentFile[]>(`${this.base}/legal_entities/${legalEntityId}/documents`);
  }

  /**
   * Generate presigned download URL for a document
   * Endpoint: GET /legal_entities/{id}/documents/{document_file_id}/download-url
   *
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID
   * @returns Presigned GET URL for downloading the file
   */
  generateDownloadUrl(
    legalEntityId: number,
    documentFileId: number,
  ): Observable<DownloadUrlResponse> {
    return this.http.get<DownloadUrlResponse>(
      `${this.base}/legal_entities/${legalEntityId}/documents/${documentFileId}/download-url`,
    );
  }

  /**
   * Trigger file download in browser using presigned URL
   * Creates a temporary anchor element and triggers download
   *
   * @param downloadUrl - Presigned download URL
   * @param filename - Original filename for download
   */
  downloadFile(downloadUrl: string, filename: string): void {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Delete a document
   * Endpoint: DELETE /legal_entities/{legal_entity_id}/documents/{document_file_id}
   *
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID to delete
   * @returns Response with deletion confirmation
   */
  deleteDocument(
    legalEntityId: number,
    documentFileId: number,
  ): Observable<{ message: string; document_file_id: number }> {
    return this.http.delete<{ message: string; document_file_id: number }>(
      `${this.base}/legal_entities/${legalEntityId}/documents/${documentFileId}`,
    );
  }
  /**
   * Initiate DDQ document upload and generate presigned URL
   * Endpoint: POST /legal_entities/{legal_entity_id}/documents/upload-url
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Upload request with document details
   * @returns Document file ID and presigned URL
   */
  generateDdqUploadUrl(
    legalEntityId: number,
    request: DdqUploadRequest,
  ): Observable<DdqUploadResponse> {
    return this.http.post<DdqUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/documents/upload-url`,
      request,
    );
  }

  /**
   * Confirm DDQ document upload
   * Endpoint: POST /legal_entities/{legal_entity_id}/ddq/confirm-upload
   *
   * @param legalEntityId - Legal entity ID
   * @param request - Confirmation request with document_file_id and optional comments
   * @returns Confirmation response with status update
   */
  confirmDdqUpload(
    legalEntityId: number,
    request: DdqConfirmUploadRequest,
  ): Observable<DdqConfirmUploadResponse> {
    return this.http.post<DdqConfirmUploadResponse>(
      `${this.base}/legal_entities/${legalEntityId}/ddq/confirm-upload`,
      request,
    );
  }

  /**
   * Approve or reject a DDQ document (PST Member action at status 11)
   * POST /legal_entities/<legal_entity_id>/ddq/<document_file_id>/<action>/pst-members
   * @param legalEntityId - Legal entity ID
   * @param documentFileId - Document file ID
   * @param action - Action to perform ('approve' or 'reject')
   * @returns Response with new status and notification info
   */
  ddqAction(
    legalEntityId: number,
    documentFileId: number,
    action: 'approve' | 'reject',
  ): Observable<DdqActionResponse> {
    return this.http.post<DdqActionResponse>(
      `${this.base}/legal_entities/${legalEntityId}/ddq/${documentFileId}/${action}/pst-members`,
      {},
    );
  }

  /**
   * Approve or reject a DDQ document (Privacy Office action at status 12)
   * POST /legal_entities/<legal_entity_id>/ddq/<action>/privacy-office
   * @param legalEntityId - Legal entity ID
   * @param action - Action to perform ('approve' or 'reject')
   * @returns Response with new status and notification info
   */
  ddqActionPrivacyOffice(
    legalEntityId: number,
    action: 'approve' | 'reject',
  ): Observable<DdqActionResponse> {
    return this.http.post<DdqActionResponse>(
      `${this.base}/legal_entities/${legalEntityId}/ddq/${action}/privacy-office`,
      {},
    );
  }

  /**
   * Get all remediation actions for a legal entity
   * Endpoint: GET /legal_entities/{legal_entity_id}/remediation-action-tracker
   * @param legalEntityId - Legal entity ID
   * @returns Observable array of remediation action DTOs
   */
  getRemediationActions(legalEntityId: number): Observable<RemediationActionDto[]> {
    this.logger.info('Fetching remediation actions', { legalEntityId });
    return this.http
      .get<RemediationActionDto[]>(`${this.endpoint}/${legalEntityId}/remediation-action-tracker`)
      .pipe(
        tap((actions) =>
          this.logger.info('Remediation actions loaded', {
            legalEntityId,
            count: actions.length,
          }),
        ),
        catchError((error) => {
          this.logger.error('Error loading remediation actions', {
            legalEntityId,
            error,
          });
          throw error;
        }),
      );
  }

  /**
   * Create a new remediation action
   * Endpoint: POST /legal_entities/remediation-action-tracker
   * @param dto - Remediation action DTO (without id)
   * @returns Observable with the created remediation action (including assigned id)
   */
  createRemediationAction(dto: RemediationActionDto): Observable<RemediationActionDto> {
    this.logger.info('Creating remediation action', { legalEntityId: dto.legal_entity_id });
    return this.http
      .post<RemediationActionDto>(`${this.endpoint}/remediation-action-tracker`, dto)
      .pipe(
        tap((created) =>
          this.logger.info('Remediation action created', {
            id: created.id,
            legalEntityId: created.legal_entity_id,
          }),
        ),
        catchError((error) => {
          this.logger.error('Error creating remediation action', {
            legalEntityId: dto.legal_entity_id,
            error,
          });
          throw error;
        }),
      );
  }

  /**
   * Update an existing remediation action
   * Endpoint: PUT /legal_entities/remediation-action-tracker
   * @param dto - Complete remediation action DTO (with id)
   * @returns Observable with the updated remediation action
   */
  updateRemediationAction(dto: RemediationActionDto): Observable<RemediationActionDto> {
    this.logger.info('Updating remediation action', {
      id: dto.id,
      legalEntityId: dto.legal_entity_id,
    });
    return this.http
      .put<RemediationActionDto>(`${this.endpoint}/remediation-action-tracker`, dto)
      .pipe(
        tap((updated) =>
          this.logger.info('Remediation action updated', {
            id: updated.id,
            legalEntityId: updated.legal_entity_id,
          }),
        ),
        catchError((error) => {
          this.logger.error('Error updating remediation action', {
            id: dto.id,
            legalEntityId: dto.legal_entity_id,
            error,
          });
          throw error;
        }),
      );
  }
}
